import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Person as PersonIcon,
  Inventory as InventoryIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { formatIdentification, formatNumberWithCommas } from '../../../utils/generalFunctions';
import { fetchClientes } from '../../../utils/generalsFetch';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';

const RECOVERY_REASONS = [
  { id: "impago", name: "Impago de cuotas" },
  { id: "vencimiento", name: "Vencimiento de plazo" },
  { id: "incumplimiento", name: "Incumplimiento de condiciones" },
  { id: "fraude", name: "Fraude o falsificación" },
  { id: "voluntario", name: "Recuperación voluntaria" },
  { id: "otro", name: "Otro motivo" }
];

const RECOVERY_PRIORITIES = [
  { id: "baja", name: "Baja", color: "success" },
  { id: "media", name: "Media", color: "warning" },
  { id: "alta", name: "Alta", color: "error" },
  { id: "urgente", name: "Urgente", color: "error" }
];

const RECOVERY_STATUSES = [
  { id: "pendiente", name: "Pendiente", color: "default" },
  { id: "en_proceso", name: "En Proceso", color: "info" },
  { id: "completada", name: "Completada", color: "success" },
  { id: "cancelada", name: "Cancelada", color: "error" },
  { id: "suspendida", name: "Suspendida", color: "warning" },
  { id: "pagado", name: "Pagado", color: "success" }
];

export default function EditarRecuperacionDialog({ open, onClose, onConfirm, recuperacion = null }) {
  const [formData, setFormData] = useState({
    motivo: '',
    prioridad: '',
    descripcion: '',
    ubicacion_actual: '',
    contacto_alternativo: '',
    observaciones: '',
    monto_recuperacion: '',
    status: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');

  useEffect(() => {
    if (open && recuperacion) {
      setFormData({
        motivo: recuperacion.motivo || '',
        prioridad: recuperacion.prioridad || '',
        descripcion: recuperacion.descripcion || '',
        ubicacion_actual: recuperacion.ubicacion_actual || '',
        contacto_alternativo: recuperacion.contacto_alternativo || '',
        observaciones: recuperacion.observaciones || '',
        monto_recuperacion: recuperacion.monto_recuperacion || '',
        status: recuperacion.status || ''
      });
      setErrors({});
    }
  }, [open, recuperacion]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.motivo) newErrors.motivo = 'El motivo es requerido';
    if (!formData.prioridad) newErrors.prioridad = 'La prioridad es requerida';
    if (!formData.descripcion) newErrors.descripcion = 'La descripción es requerida';
    if (!formData.ubicacion_actual) newErrors.ubicacion_actual = 'La ubicación es requerida';
    if (!formData.monto_recuperacion) newErrors.monto_recuperacion = 'El monto es requerido';
    if (!formData.status) newErrors.status = 'El estado es requerido';

    if (formData.monto_recuperacion && (isNaN(formData.monto_recuperacion) || formData.monto_recuperacion <= 0)) {
      newErrors.monto_recuperacion = 'El monto debe ser un número válido mayor a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setMessageAlert('Por favor, corrige los errores en el formulario.');
      setOpenAlert(true);
      return;
    }

    setLoading(true);
    try {
      await onConfirm({
        ...recuperacion,
        ...formData,
        monto_recuperacion: parseFloat(formData.monto_recuperacion)
      });
      
      setMessageAlert('Recuperación actualizada exitosamente.');
      setOpenAlert(true);
      onClose();
    } catch (error) {
      setMessageAlert('Error al actualizar la recuperación.');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      motivo: '',
      prioridad: '',
      descripcion: '',
      ubicacion_actual: '',
      contacto_alternativo: '',
      observaciones: '',
      monto_recuperacion: '',
      status: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #55b4ac 0%, #4a9b94 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <EditIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight="bold">
              Editar Recuperación
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {recuperacion && (
            <Box mb={3} mt={2}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Cliente: {recuperacion.cliente?.nombre || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Producto: {recuperacion.producto?.nombre || 'N/A'} | Préstamo: {recuperacion.prestamo_id || 'N/A'}
              </Typography>
            </Box>
          )}

          <Grid container spacing={3}>
            {/* Motivo */}
            <Grid item size={6}>
              <FormControl fullWidth error={!!errors.motivo}>
                <Typography variant="body1" fontWeight={600} ml={1}>Motivo de Recuperación</Typography>
                <Select
                  value={formData.motivo}
                  onChange={(e) => handleInputChange('motivo', e.target.value)}
                >
                  {RECOVERY_REASONS.map((reason) => (
                    <MenuItem key={reason.id} value={reason.id}>
                      {reason.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.motivo && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.motivo}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Prioridad */}
            <Grid item size={6}>
              <FormControl fullWidth error={!!errors.prioridad}>
                <Typography variant="body1" fontWeight={600} ml={1}>Prioridad</Typography>
                <Select
                  value={formData.prioridad}
                  onChange={(e) => handleInputChange('prioridad', e.target.value)}
                >
                  {RECOVERY_PRIORITIES.map((priority) => (
                    <MenuItem key={priority.id} value={priority.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip 
                          label={priority.name} 
                          color={priority.color} 
                          size="small"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.prioridad && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.prioridad}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Estado */}
            <Grid item size={6}>
              <FormControl fullWidth error={!!errors.status}>
                <Typography variant="body1" fontWeight={600} ml={1}>Estado</Typography>
                <Select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  {RECOVERY_STATUSES.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip 
                          label={status.name} 
                          color={status.color} 
                          size="small"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.status && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.status}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Monto de Recuperación */}
            <Grid item size={6}>
             <Typography variant="body1" fontWeight={600} ml={1}>Monto de Recuperación</Typography>
              <TextField
                fullWidth
                type="number"
                value={formData.monto_recuperacion}
                onChange={(e) => handleInputChange('monto_recuperacion', e.target.value)}
                error={!!errors.monto_recuperacion}
                helperText={errors.monto_recuperacion}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
              />
            </Grid>

            {/* Descripción */}
            <Grid item size={12}>
                <Typography variant="body1" fontWeight={600} ml={1}>Descripción</Typography>
              <TextField
                fullWidth
                multiline
                value={formData.descripcion}
                onChange={(e) => handleInputChange('descripcion', e.target.value)}
                error={!!errors.descripcion}
                helperText={errors.descripcion}
                placeholder="Describe los detalles de la recuperación..."
              />
            </Grid>

            {/* Ubicación Actual */}
            <Grid item size={6}>
                <Typography variant="body1" fontWeight={600} ml={1}>Ubicación Actual</Typography>
              <TextField
                fullWidth
                value={formData.ubicacion_actual}
                onChange={(e) => handleInputChange('ubicacion_actual', e.target.value)}
                error={!!errors.ubicacion_actual}
                helperText={errors.ubicacion_actual}
                placeholder="Dirección donde se encuentra el producto..."
              />
            </Grid>

            {/* Contacto Alternativo */}
            <Grid item size={6}>
                <Typography variant="body1" fontWeight={600} ml={1}>Contacto Alternativo</Typography>
              <TextField
                fullWidth
                value={formData.contacto_alternativo}
                onChange={(e) => handleInputChange('contacto_alternativo', e.target.value)}
                placeholder="Teléfono o email de contacto..."
              />
            </Grid>

            {/* Observaciones */}
            <Grid item size={12}>
                <Typography variant="body1" fontWeight={600} ml={1}>Observaciones</Typography>
              <TextField
                fullWidth
                multiline
                value={formData.observaciones}
                onChange={(e) => handleInputChange('observaciones', e.target.value)}
                placeholder="Observaciones adicionales sobre la recuperación..."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ fontWeight: 'bold' }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <EditIcon />}
            color="primarybutton"
            sx={{ 
              fontWeight: 'bold',
            }}
          >
            {loading ? 'Actualizando...' : 'Actualizar Recuperación'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessageDialog 
        message={messageAlert} 
        title="" 
        severity="success" 
        color="success" 
        open={openAlert} 
        handleClose={() => setOpenAlert(false)}
      />
    </>
  );
}
